import { ReactComponent as CloseIcon } from 'assets/icons/trash16.svg'
import { handbookLink } from 'components/Handbook2_0/util'
import useHandbookItemDeleteConfirmationModal from 'hooks/useHandbookItemDeleteConfirmationModal'
import { useHistory, useLocation } from 'react-router-dom'
import { useRootStore } from 'stores/RootStore'
import { HandbookItem, HandbookItemType } from 'types'
import { ActionHandler, ActionKey, MenuItem } from '../../Types'
import { useFinderRefresh } from 'screens/Finder/hooks/useFinderRefresh'

const createRemovable = (label?: string) => [
  { key: ActionKey.delete, textValue: label || 'Remove', Icon: CloseIcon } as const,
]

interface Removable {
  removable: MenuItem[]
  handleRemove: ActionHandler
}

export const useRemovable = (item: HandbookItem, actionString?: string): Removable => {
  const history = useHistory()
  const location = useLocation()
  const docStore = useRootStore().docStore
  const refreshFinder = useFinderRefresh()
  const handbooksStore = useRootStore().handbooks
  const treeItem = handbooksStore.items.get(item.id.toString())
  const confirmationNeeded =
    treeItem?.hasChildren || [HandbookItemType.Document, HandbookItemType.NestedHandbook].includes(item.type)

  const openConfirmModal = useHandbookItemDeleteConfirmationModal()

  const redirectToParent = () => {
    if (!docStore || docStore.articleId !== item.document?.key) {
      return
    }
    const parentSeoString = treeItem?.parentHandbook?.handbook.seo_string
    if (treeItem?.parentHandbook) {
      history.push({
        pathname: parentSeoString ? handbookLink(parentSeoString, undefined, true) : '/',
        search: location.search,
      })
    }
  }

  const handleRemove = (actionKey: ActionKey) => {
    if (actionKey !== ActionKey.delete || !treeItem) return false
    if (confirmationNeeded && treeItem.parentHandbook) {
      openConfirmModal({
        item,
        onDeleteNested: async () => {
          if (!treeItem) return

          await treeItem.delete()
          treeItem.parentHandbook?.refresh()
          refreshFinder()
          redirectToParent()
        },
        onDeleteAndReparent: async () => {
          if (!treeItem) return

          await treeItem.delete(true)
          treeItem.parentHandbook?.refresh()
          refreshFinder()
          redirectToParent()
        },
      })
    } else {
      redirectToParent()
      treeItem.delete()
    }
    return true
  }

  return {
    removable: createRemovable(actionString),
    handleRemove,
  }
}
